import React, { useEffect, useRef, useState } from "react";
import IconButton from "../base/IconButton";
import FlexRow from "../base/FlexRow";

type VoiceRecorderProps = {
    onClose: () => void;
    onSubmit: (file: File) => void;
};

const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;

    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function VoiceRecorder({ onClose, onSubmit }: VoiceRecorderProps) {

    const audioChunksRef = useRef<Float32Array[]>([]);
    const processorRef = useRef<ScriptProcessorNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

    const streamRef = useRef<MediaStream | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const animationRef = useRef<number | null>(null);
    const timerRef = useRef<number | null>(null);

    const [seconds, setSeconds] = useState(0);
    const [wavePoints, setWavePoints] = useState<number[]>([]);

    const waveContainerRef = useRef<HTMLDivElement | null>(null);
    const maxBarsRef = useRef(80);

    useEffect(() => {

        startRecording();

        const updateMaxBars = () => {
            const width = waveContainerRef.current?.clientWidth || 0;
            // each bar = 3px width + 3px gap
            maxBarsRef.current = Math.max(20, Math.floor(width / 6));
        };

        updateMaxBars();

        window.addEventListener("resize", updateMaxBars);

        return () => {
            window.removeEventListener("resize", updateMaxBars);
            cleanup();
        };

    }, []);

    const startRecording = async () => {
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                console.error("Microphone access is not available.");
                onClose();
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            audioChunksRef.current = [];

            const AudioContextClass =
                window.AudioContext || (window as any).webkitAudioContext;

            const audioContext = new AudioContextClass();

            audioContextRef.current = audioContext;

            await audioContext.resume();

            const source = audioContext.createMediaStreamSource(stream);
            sourceRef.current = source;

            const processor = audioContext.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;

            processor.onaudioprocess = (event) => {
                const inputData = event.inputBuffer.getChannelData(0);

                audioChunksRef.current.push(new Float32Array(inputData));
            };

            source.connect(processor);
            processor.connect(audioContext.destination);

            startTimer();
            startWave(stream);


        } catch (error) {
            console.error("Microphone permission denied or microphone not found.", error);
            onClose();
        }
    };

    const startTimer = () => {
        timerRef.current = window.setInterval(() => {
            setSeconds((prev) => prev + 1);
        }, 1000);
    };

    const startWave = async (stream: MediaStream) => {
        try {
            const AudioContextClass =
                window.AudioContext || (window as any).webkitAudioContext;

            if (!AudioContextClass) {
                console.error("AudioContext is not supported.");
                return;
            }

            const audioContext = new AudioContextClass();
            audioContextRef.current = audioContext;

            if (audioContext.state === "suspended") {
                await audioContext.resume();
            }

            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();

            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.75;

            source.connect(analyser);

            const dataArray = new Uint8Array(analyser.fftSize);

            const draw = () => {
                analyser.getByteTimeDomainData(dataArray);

                let sum = 0;

                for (let i = 0; i < dataArray.length; i++) {
                    const value = (dataArray[i] - 128) / 128;
                    sum += value * value;
                }

                const rms = Math.sqrt(sum / dataArray.length);

                // Android WebView sometimes returns very small values
                const amplified = Math.min(1, rms * 8);

                // setWavePoints((prev) => {
                //     const next = [...prev, amplified];

                //     if (next.length > 90) {
                //         next.shift();
                //     }

                //     return next;
                // });

                setWavePoints((prev) => {
                    const next = [...prev, amplified];
                    const maxBars = maxBarsRef.current;

                    while (next.length > maxBars) {
                        next.shift();
                    }

                    return next;
                });

                animationRef.current = requestAnimationFrame(draw);
            };

            draw();
        } catch (error) {
            console.error("Wave analyser failed.", error);
        }
    };


    const cleanup = () => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        if (audioContextRef.current && audioContextRef.current.state !== "closed") {
            audioContextRef.current.close();
        }

        processorRef.current?.disconnect();
        sourceRef.current?.disconnect();

        streamRef.current?.getTracks().forEach((track) => track.stop());

        animationRef.current = null;
        timerRef.current = null;
        audioContextRef.current = null;
        streamRef.current = null;
        processorRef.current = null;
        sourceRef.current = null;
        audioChunksRef.current = [];
    };

    const handleCancel = () => {
        cleanup();
        onClose();
    };

    const handleSubmit = async () => {

        const audioContext = audioContextRef.current;

        if (!audioContext) {
            onClose();
            return;
        }

        const mergedBuffer = mergeBuffers(audioChunksRef.current);

        const wavBuffer = encodeWav(
            mergedBuffer,
            audioContext.sampleRate
        );

        const blob = new Blob([wavBuffer], {
            type: "audio/wav",
        });

        const file = new File(
            [blob],
            `voice-${Date.now()}.wav`,
            {
                type: "audio/wav",
            }
        );

        cleanup();

        onSubmit(file);

        onClose();
    };



    const mergeBuffers = (buffers: Float32Array[]) => {

        let totalLength = 0;

        for (const buffer of buffers) {
            totalLength += buffer.length;
        }

        const result = new Float32Array(totalLength);

        let offset = 0;

        for (const buffer of buffers) {
            result.set(buffer, offset);
            offset += buffer.length;
        }

        return result;
    };

    const encodeWav = (
        samples: Float32Array,
        sampleRate: number
    ) => {

        const buffer = new ArrayBuffer(44 + samples.length * 2);

        const view = new DataView(buffer);

        const writeString = (
            offset: number,
            str: string
        ) => {
            for (let i = 0; i < str.length; i++) {
                view.setUint8(offset + i, str.charCodeAt(i));
            }
        };

        writeString(0, "RIFF");

        view.setUint32(4, 36 + samples.length * 2, true);

        writeString(8, "WAVE");

        writeString(12, "fmt ");

        view.setUint32(16, 16, true);

        view.setUint16(20, 1, true);

        view.setUint16(22, 1, true);

        view.setUint32(24, sampleRate, true);

        view.setUint32(28, sampleRate * 2, true);

        view.setUint16(32, 2, true);

        view.setUint16(34, 16, true);

        writeString(36, "data");

        view.setUint32(40, samples.length * 2, true);

        let offset = 44;

        for (let i = 0; i < samples.length; i++) {

            const s = Math.max(-1, Math.min(1, samples[i]));

            view.setInt16(
                offset,
                s < 0 ? s * 0x8000 : s * 0x7fff,
                true
            );

            offset += 2;
        }

        return buffer;
    };


    return (
        <div
            className="d-flex align-items-center w-100 px-3 gap-3"
            style={{
                height: 55,
            }}
        >
            <div className="fw-bold"
                style={{
                    minWidth: 40
                }}
            >
                {formatTime(seconds)}
            </div>

            <div ref={waveContainerRef}
                className="flex-grow-1 position-relative overflow-hidden"
                style={{
                    height: 36,
                    overflowX: "hidden",
                }}
            >
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: "50%",
                        height: 2,
                        background: "#d7d7d7",
                        transform: "translateY(-50%)",
                        borderRadius: 10,
                    }}
                />

                <div
                    className="d-flex align-items-center"
                    style={{
                        height: "100%",
                        gap: 3,
                    }}
                >
                    {wavePoints.map((point, index) => {
                        const height = 3 + point * 34;

                        return (
                            <span
                                key={index}
                                style={{
                                    width: 3,
                                    height,
                                    minHeight: 3,
                                    borderRadius: 10,
                                    background: "#25d366",
                                    display: "inline-block",
                                    transition: "height 70ms linear",
                                }}
                            />
                        );
                    })}

                </div>
            </div>

            <FlexRow justify="end" style={{
                minWidth: 100
            }}>
                <IconButton icon="stop" type="solid" className="bg-light-danger text-danger me-1"
                    size={16} gap={20}
                    onClick={handleCancel} />

                <IconButton className="bg-primary text-white" icon="send" type="solid"
                    size={18} gap={26}
                    onClick={handleSubmit} />
            </FlexRow>

        </div>
    );
}